import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { useDealContext, useMutateDealContext } from "~/context/DealContext";
import type {
	WithdrawalReasonCategory,
	WithdrawFormValues
} from "~/models/DealPartnerAssignment";
import {
	WITHDRAWAL_REASON_CATEGORIES,
	WITHDRAWAL_REASON_OPTIONS,
	withdrawPartnerAssignmentSchema
} from "~/models/DealPartnerAssignment";
import { isSuccess } from "~/utils/api/api_handler";
import {
	paths,
	withdraw
} from "~/utils/api/vendors/deals/DealPartnerAssignment";

type Props = DeepReadonly<{
	handleClose: () => void;
}>;

export function WithdrawPartnerAssignmentModal({ handleClose }: Props) {
	const { notify } = useNotification();
	const deal = useDealContext();
	const mutateDeal = useMutateDealContext();
	const {
		register,
		handleSubmit,
		control,
		formState: { errors }
	} = useForm<WithdrawFormValues>({
		defaultValues: {
			withdrawalReasonCategory: "",
			withdrawalReasonDetail: ""
		},
		resolver: yupResolver(withdrawPartnerAssignmentSchema)
	});

	const withdrawalReasonCategory = useWatch({
		control,
		name: "withdrawalReasonCategory"
	});

	const { trigger, isMutating } = useSWRMutation(
		paths.withdraw(deal.id),
		async (_key, { arg }: { arg: WithdrawFormValues }) =>
			withdraw(deal.id, {
				withdrawalReasonCategory:
					arg.withdrawalReasonCategory as WithdrawalReasonCategory,
				withdrawalReasonDetail: arg.withdrawalReasonDetail
			})
	);

	const onSubmit = useCallback(
		async (data: WithdrawFormValues) => {
			const result = await trigger(data);
			if (isSuccess(result)) {
				await mutateDeal();
				notify({ content: "依頼を取り下げました。", variant: "success" });
				handleClose();
			} else {
				notify({
					content: result.message ?? "エラーが発生しました。",
					variant: "error"
				});
			}
		},
		[trigger, mutateDeal, notify, handleClose]
	);

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="依頼の取り下げ" />
			<form noValidate onSubmit={handleSubmit(onSubmit)}>
				<ModalBody>
					<FormGroup variant="vertical">
						<FormControl
							chip="必須"
							isInvalid={!!errors.withdrawalReasonCategory}
							label="取り下げ理由"
						>
							<InputSelect
								isInvalid={!!errors.withdrawalReasonCategory}
								{...register("withdrawalReasonCategory")}
							>
								<option value="">選択してください</option>
								{WITHDRAWAL_REASON_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</InputSelect>
							<FormControlText
								error={errors.withdrawalReasonCategory?.message}
							/>
						</FormControl>
						<FormControl
							chip={
								withdrawalReasonCategory === WITHDRAWAL_REASON_CATEGORIES.OTHER
									? "必須"
									: undefined
							}
							isInvalid={!!errors.withdrawalReasonDetail}
							label="詳細理由"
						>
							<InputText
								isInvalid={!!errors.withdrawalReasonDetail}
								placeholder="取り下げについての詳細な理由を入力してください"
								resize="autoRows"
								rows={4}
								{...register("withdrawalReasonDetail")}
							/>
							<FormControlText error={errors.withdrawalReasonDetail?.message} />
						</FormControl>
					</FormGroup>
				</ModalBody>
				<ModalFooter
					leftArea={
						<Button
							disabled={isMutating}
							onClick={handleClose}
							variant="secondary"
						>
							キャンセル
						</Button>
					}
					rightArea={
						<Button loading={isMutating} type="submit" variant="danger">
							取り下げ
						</Button>
					}
				/>
			</form>
		</Modalv2>
	);
}
